import type { paths } from "@/api-schema";
import { Action, PathsV1UserUuidGetParametersQueryExtend, UserStatus, UserType } from "@/api-schema-enums";
import { Can } from "@/casl-provider";
import { useUserByUuidQuery } from "@/common/api/user/use-user-by-uuid.query";
import { useUserActions } from "@/common/hooks/user/use-user-actions";
import { UserAppRoleTable } from "@/components/data-table/predefined/user-app-role-table";
import { DataTableHeightMode } from "@/components/data-table/types";
import { DetailHeader } from "@/components/detail-page/detail-header";
import { DetailPageLayout } from "@/components/detail-page/detail-page-layout";
import { DetailTabs } from "@/components/detail-page/detail-tabs";
import { SystemIdTab } from "@/components/detail-page/tabs/system-id-tab";
import type { DetailTab } from "@/components/detail-page/types";
import { CenteredLayout, CenteredLayoutVariant } from "@/components/layouts/centered-layout";
import { Badge, BadgeColorVariant, BadgeVariant } from "@/components/ui/badge";
import { AvatarClickAction, UserAvatar } from "@/components/user-avatar/user-avatar";
import { UuidExistenceWrapper } from "@/components/uuid-existence-wrapper/uuid-existence-wrapper";
import { Building2, Fingerprint, KeyRound, Network, UserRound } from "lucide-react";
import type { ReactElement } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "wouter";
import { UserDetailSkeleton } from "./_components/user-detail-skeleton";
import { HierarchyTab } from "./tabs/hierarchy-tab/hierarchy-tab";
import { OrganizationTab } from "./tabs/organization-tab/organization-tab";
import { OverviewTab } from "./tabs/overview-tab/overview-tab";

const USER_DETAIL_EXTENDS = [
  PathsV1UserUuidGetParametersQueryExtend.Address,
  PathsV1UserUuidGetParametersQueryExtend.Brands,
  PathsV1UserUuidGetParametersQueryExtend.Branches,
  PathsV1UserUuidGetParametersQueryExtend.Branches_Company,
  PathsV1UserUuidGetParametersQueryExtend.Branches_Brands,
  PathsV1UserUuidGetParametersQueryExtend.Branches_Address,
  PathsV1UserUuidGetParametersQueryExtend.Companies,
  PathsV1UserUuidGetParametersQueryExtend.Departments,
  PathsV1UserUuidGetParametersQueryExtend.JobFunctions,
  PathsV1UserUuidGetParametersQueryExtend.Occupations,
  PathsV1UserUuidGetParametersQueryExtend.MainBranch,
  PathsV1UserUuidGetParametersQueryExtend.MainBranch_Company,
  PathsV1UserUuidGetParametersQueryExtend.MainBranch_Brands,
  PathsV1UserUuidGetParametersQueryExtend.MainBranch_Address,
] as const;

const USER_DETAIL_QUERY_PARAMS = { parameters: { extend: [...USER_DETAIL_EXTENDS] } };

const USER_DETAIL_TRACKING_NAME = "user";

export function UserDetailView(): ReactElement {
  const { t } = useTranslation(["masterdata"]);
  const params = useParams<{ userUuid: string }>();
  const userUuid = params.userUuid ?? "";

  return (
    <Can I="read" a="User">
      <UuidExistenceWrapper<
        paths["/v1/user/{uuid}"]["get"]["parameters"]["query"],
        paths["/v1/user/{uuid}"]["get"]["responses"][200]["content"]["application/json"]
      >
        uuid={userUuid}
        query={useUserByUuidQuery}
        notFoundProps={{
          title: t("masterdata:UserNotFound"),
          message: t("masterdata:UserDoesNotExist"),
          description: t("masterdata:CheckUserIdOrContactSupport"),
          backTo: "/",
          backToLabel: t("masterdata:BackToUserOverview"),
        }}
      >
        <UserDetailContent userUuid={userUuid} />
      </UuidExistenceWrapper>
    </Can>
  );
}

function UserDetailContent({ userUuid }: { userUuid: string }): ReactElement {
  const { t } = useTranslation(["masterdata", "common", "ram"]);
  const { data: user, isPending, isError } = useUserByUuidQuery(userUuid, USER_DETAIL_QUERY_PARAMS);
  const { getActionsForUser, dialogs } = useUserActions();

  if (isPending) {
    return <UserDetailSkeleton />;
  }

  if (isError || !user) {
    return (
      <CenteredLayout
        variant={CenteredLayoutVariant.Default}
        title={t("masterdata:UserInformation")}
        subTitle={t("masterdata:UserDataCouldNotBeLoaded")}
      >
        {null}
      </CenteredLayout>
    );
  }

  const fullName = [user.lastName, user.firstName].filter(Boolean).join(", ");

  const tabs: DetailTab[] = [
    {
      id: "overview",
      label: t("common:Overview"),
      icon: UserRound,
      content: () => <OverviewTab user={user} />,
    },
    {
      id: "organization",
      label: t("common:Organization"),
      icon: Building2,
      content: () => <OrganizationTab user={user} />,
    },
    {
      id: "hierarchy",
      label: t("masterdata:Hierarchy"),
      icon: Network,
      content: () => <HierarchyTab userUuid={user.uuid} />,
    },
    {
      id: "app-roles",
      label: t("ram:AppRoles"),
      icon: KeyRound,
      permission: { action: Action.Manage, subject: "AppRole" },
      content: () => <UserAppRoleTable userUuid={user.uuid} heightMode={DataTableHeightMode.Content} />,
    },
    {
      id: "identifiers",
      label: t("masterdata:SystemIds"),
      icon: Fingerprint,
      content: () => (
        <SystemIdTab
          uuid={user.uuid}
          createdAt={user.createdAt}
          updatedAt={user.updatedAt}
          deletedAt={user.deletedAt ?? undefined}
          identifiers={
            user.azureObjectUuid ? [{ id: "azure", label: t("masterdata:AzureId"), value: user.azureObjectUuid }] : []
          }
        />
      ),
    },
  ];

  return (
    <>
      <DetailPageLayout
        variant={CenteredLayoutVariant.Wide}
        header={
          <DetailHeader
            icon={
              <UserAvatar
                userAzureObjectUuid={user.azureObjectUuid}
                name={fullName || undefined}
                clickAction={AvatarClickAction.Zoom}
                className="size-12 rounded-lg"
              />
            }
            title={fullName || t("masterdata:UserInformation")}
            subtitle={
              user.email ? (
                <a href={`mailto:${user.email}`} className="underline-offset-4 hover:underline">
                  {user.email}
                </a>
              ) : undefined
            }
            badges={
              <>
                <Badge
                  variant={BadgeVariant.Outlined}
                  colorVariant={
                    user.type === UserType.Employee ? BadgeColorVariant.Primary : BadgeColorVariant.Secondary
                  }
                >
                  {user.type === UserType.Employee ? t("masterdata:Employee") : t("masterdata:Guest")}
                </Badge>
                {user.status === UserStatus.Disabled && (
                  <Badge variant={BadgeVariant.Outlined} colorVariant={BadgeColorVariant.Error}>
                    {t("common:Disabled")}
                  </Badge>
                )}
              </>
            }
            actions={getActionsForUser(user)}
          />
        }
      >
        <DetailTabs sections={tabs} trackingName={USER_DETAIL_TRACKING_NAME} />
      </DetailPageLayout>
      {dialogs}
    </>
  );
}
